import {
  ADJACENT_PREFETCH_RANGES,
  resolveAssessmentsRange,
} from "./TeamsDashboardView.helpers";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  ChevronRight,
  Frown,
  Loader2,
  Minus,
  PhoneCall,
  Smile,
  Sparkles,
} from "lucide-react";
import { MeasureBarChart, type MeasureFocus } from "../components/MeasureBarChart";
import { FocusedMeasureAgentPanel } from "../components/FocusedMeasureAgentPanel";
import { TRIAGE_TOOLTIP_COPY, LOW_SAMPLE_TOOLTIP } from "../components/TeamTriageList";
import { Tooltip } from "../components/Tooltip";
import { type MetricId } from "../components/MetricTooltips.helpers";
import { LabelWithTooltip } from "../components/MetricTooltips";

import {
  cohortAveragePerLabel,
  isLowSample,
  overallDetectionRate,
  severityTier,
  triageSort,
} from "@/lib/triage";
import {
  TeamCallsByAssessmentStackedBar,
  TeamTrendLineChart,
} from "../components/AssessmentCharts";
import { CallCountCaption } from "../components/CallCountCaption";
import {
  api,
  type AgentCallSlice,
  type AnalyticsAgentRow,
  type AnalyticsDashboard,
  type AnalyticsTeamAgent,
  type AnalyticsTeamOption,
  type AnalyticsMetricDelta,
  type AnalyticsTeamAgentSentimentRow,
  type AnalyticsTeamBreakdown,
  type AssessmentAggregate,
  type CustomDateRange,
  type RangeMode,
  type Role,
  type TeamAggregate,
  type TimeRange,
  type UserOut,
} from "@/lib/api";
import { agentSlug } from "@/lib/agents.helpers";

import { fmtDurationMs } from "@/lib/format";
import { rangeTitle } from "@/components/TimeRangeControls.helpers";
import { TimeRangeControls } from "@/components/TimeRangeControls";

import { summariseMeasures } from "@/views/AgentProfileView.helpers";
import { AssessmentSummaryStrip } from "@/views/AgentProfileView";

import { CollapsibleSection } from "@/components/CollapsibleSection";
import {
  TeamVolumeCard,
  type FunnelFilter,
  type SentimentFilter,
} from "@/components/TeamVolumeCard";
import { agentProfileHref, parseUrl } from "@/lib/router";
import { useTimeRange } from "@/lib/useTimeRange";

export type AgentDrillNav = (
  slug: string,
  key: string,
  opts: {
    slice: AgentCallSlice | null;
    team: string | null;
    timeRange?: TimeRange | null;
    rangeMode?: RangeMode | null;
    // Task #365 — paired YYYY-MM-DD UTC bounds for the "custom"
    // preset. Both must be supplied together; otherwise the agent
    // profile silently falls back to its default range.
    customStart?: string | null;
    customEnd?: string | null;
    // Task #290 — when set, the AgentProfileView's Assessment
    // performance section opens with this measure pre-focused via
    // the existing ``mid``/``ln`` URL deep-link.
    focusedMeasureId?: string | null;
    focusedLabelName?: string | null;
  },
) => void;

// Compact percent formatter — single source of truth so the cards, funnel
// labels, and table cells all round identically.
function pct(part: number, whole: number): string {
  if (!whole) return "—";
  return `${((part / whole) * 100).toFixed(1)}%`;
}

function pctNum(part: number, whole: number): number {
  if (!whole) return 0;
  return (part / whole) * 100;
}

function DeltaBadge({ delta }: { delta: AnalyticsMetricDelta | undefined }) {
  if (!delta) return null;
  const { absolute, pct: pctChange } = delta;
  // "Flat" covers both a 0 absolute change and the rare case where a
  // metric goes from 0 → 0 (no signal in either direction).
  const flat = absolute === 0;
  const up = absolute > 0;
  const Icon = flat ? Minus : up ? ArrowUp : ArrowDown;
  const tone = flat
    ? "bg-muted text-muted-foreground"
    : up
    ? "bg-primary/15 text-primary"
    : "bg-destructive/15 text-destructive";
  // No prior value → percent is undefined; show the absolute delta only.
  const label =
    pctChange == null
      ? `${absolute >= 0 ? "+" : ""}${absolute.toLocaleString()}`
      : `${pctChange >= 0 ? "+" : ""}${pctChange.toFixed(1)}%`;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] font-semibold leading-none tabular-nums ${tone}`}
      title={`${absolute >= 0 ? "+" : ""}${absolute.toLocaleString()} vs prior period`}
    >
      <Icon className="h-3 w-3 shrink-0" strokeWidth={2.5} aria-hidden />
      {label}
    </span>
  );
}

function MetricCard({
  label,
  value,
  sub,
  icon,
  tone = "slate",
  delta,
  tooltip,
  metricId,
  selected = false,
  onClick,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  tone?: "slate" | "emerald" | "rose" | "indigo" | "amber";
  delta?: AnalyticsMetricDelta;
  tooltip?: string;
  // Task #316 — canonical metric id used to render an "i" indicator
  // next to the tile label; copy comes from ``METRIC_TOOLTIPS``. When
  // omitted (or alongside the legacy ``tooltip`` icon-hover) the
  // label renders plainly.
  metricId?: MetricId;
  selected?: boolean;
  onClick?: () => void;
}) {
  const toneClasses: Record<string, string> = {
    slate: "bg-muted text-foreground",
    emerald: "bg-primary/15 text-primary",
    rose: "bg-destructive/15 text-destructive",
    indigo: "bg-secondary/15 text-secondary",
    amber: "bg-accent/20 text-accent-foreground",
  };
  const clickable = typeof onClick === "function";
  const baseClasses =
    "flex h-full w-full text-left flex-col rounded-lg border bg-card p-4 shadow-md animate-card-enter transition-all duration-[var(--duration-base)] ease-[var(--easing-standard)] hover:shadow-md";
  const stateClasses = selected
    ? "border-primary ring-2 ring-primary/40 bg-primary/5"
    : "border-border";
  const interactiveClasses = clickable
    ? "cursor-pointer hover:border-primary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    : "";
  const inner = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {metricId ? (
            <LabelWithTooltip metricId={metricId}>{label}</LabelWithTooltip>
          ) : (
            label
          )}
        </div>
        <div
          className={`shrink-0 rounded-md p-1.5 ${toneClasses[tone]} ${tooltip ? "cursor-help" : ""}`}
          title={tooltip}
        >
          {icon}
        </div>
      </div>
      <div
        className="mt-2 text-4xl font-bold text-primary leading-tight tabular-nums"
        style={{ fontFamily: "var(--app-font-serif)" }}
      >
        {value}
      </div>
      <div className="mt-2 flex min-h-[1.25rem] items-center justify-between gap-3 border-t border-border/60 pt-2">
        <span className="text-xs text-muted-foreground truncate">{sub ?? ""}</span>
        <DeltaBadge delta={delta} />
      </div>
    </>
  );
  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={`${baseClasses} ${stateClasses} ${interactiveClasses}`}
      >
        {inner}
      </button>
    );
  }
  return <div className={`${baseClasses} ${stateClasses}`}>{inner}</div>;
}

type TeamFunnelRow = {
  team_id: string;
  team_name: string;
  registered: number;
  viable: number;
  quality: number;
};

function TeamCallFunnelBars({ rows }: { rows: TeamFunnelRow[] }) {
  // Compact per-team funnel readout: each row shows the team name and
  // three colored chips for Registered / Viable / Quality with the count
  // and percentage of Registered. No bar graphic — the numbers are the
  // primary signal.
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="text-sm font-semibold text-foreground mb-1">
        Dialpad call funnel by team
      </h3>
      <div
        className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
        aria-hidden="true"
      >
        <LegendSwatch colorClass="bg-muted-foreground" label="Registered" />
        <LegendSwatch colorClass="bg-secondary" label="Viable" />
        <LegendSwatch colorClass="bg-accent" label="Quality" />
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">No teams in scope.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const segments = [
              {
                key: "registered",
                name: "Registered",
                count: r.registered,
                colorClass: "bg-muted-foreground",
              },
              {
                key: "viable",
                name: "Viable",
                count: r.viable,
                colorClass: "bg-secondary",
              },
              {
                key: "quality",
                name: "Quality",
                count: r.quality,
                colorClass: "bg-accent",
              },
            ];
            return (
              <div
                key={r.team_id}
                className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
              >
                <div className="truncate text-xs text-foreground">
                  {r.team_name}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:justify-end">
                  {r.registered === 0 ? (
                    <span className="text-muted-foreground">No calls</span>
                  ) : (
                    segments.map((s) => (
                      <span
                        key={s.key}
                        className="inline-flex items-center gap-1.5 tabular-nums"
                        title={`${s.name} ${s.count.toLocaleString()} (${pct(s.count, r.registered)})`}
                      >
                        <span
                          className={`inline-block h-2 w-2 rounded-sm ${s.colorClass}`}
                          aria-hidden="true"
                        />
                        <span className="text-foreground font-medium">
                          {s.count.toLocaleString()}
                        </span>
                        <span className="text-muted-foreground">
                          ({pct(s.count, r.registered)})
                        </span>
                      </span>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LegendSwatch({ colorClass, label }: { colorClass: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={`inline-block h-2 w-2 rounded-sm ${colorClass}`}
        aria-hidden="true"
      />
      <span className="text-foreground">{label}</span>
    </span>
  );
}

export type SentimentKey = "positive" | "negative" | "neutral";

const SENTIMENT_LABEL: Record<SentimentKey, string> = {
  positive: "Positive",
  negative: "Negative",
  neutral: "Neutral",
};

function TeamSentimentChart({
  rows,
  onSelect,
}: {
  rows: AnalyticsTeamBreakdown[];
  onSelect: (
    team: AnalyticsTeamBreakdown,
    sentiment: SentimentKey,
    target: HTMLElement,
  ) => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground">
          Sentiment by team
        </h3>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-primary" />
            Positive
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-destructive" />
            Negative
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-muted" />
            Neutral
          </span>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">No teams in scope.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const posPct = pctNum(r.positive, r.viable);
            const negPct = pctNum(r.negative, r.viable);
            const neuPct = Math.max(0, 100 - posPct - negPct);
            const neutralCount = Math.max(0, r.viable - r.positive - r.negative);
            const mkSegment = (
              key: SentimentKey,
              width: number,
              count: number,
              colorClass: string,
              pctLabel: string,
            ) => {
              const disabled = !r.viable;
              const label = SENTIMENT_LABEL[key];
              const btn = (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={(e) =>
                    onSelect(r, key, e.currentTarget as HTMLElement)
                  }
                  aria-label={`${label} calls for ${r.team_name} — ${pctLabel}, ${count} of ${r.viable}. Click for per-agent breakdown.`}
                  className={`h-full w-full ${colorClass} ${
                    disabled
                      ? "cursor-default"
                      : "cursor-pointer hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card"
                  }`}
                />
              );
              // Tooltip can't reliably bind focus/hover handlers to a
              // disabled <button>, and a disabled segment also has no
              // useful action — render it bare in that case.
              if (disabled) {
                return (
                  <span
                    key={key}
                    className="h-full inline-block"
                    style={{ width: `${width}%` }}
                  >
                    {btn}
                  </span>
                );
              }
              return (
                <Tooltip
                  key={key}
                  content={`${label}: ${pctLabel} — click for per-agent breakdown`}
                  wrapperClassName="h-full inline-block"
                  wrapperStyle={{ width: `${width}%` }}
                >
                  {btn}
                </Tooltip>
              );
            };
            return (
              <div key={r.team_id} className="flex items-center gap-3">
                <div className="w-40 shrink-0 truncate text-xs text-foreground">
                  {r.team_name}
                </div>
                <div className="flex-1 h-5 bg-muted rounded overflow-hidden flex">
                  {mkSegment(
                    "positive",
                    posPct,
                    r.positive,
                    "bg-primary",
                    pct(r.positive, r.viable),
                  )}
                  {mkSegment(
                    "neutral",
                    neuPct,
                    neutralCount,
                    "bg-muted",
                    `${neuPct.toFixed(1)}%`,
                  )}
                  {mkSegment(
                    "negative",
                    negPct,
                    r.negative,
                    "bg-destructive",
                    pct(r.negative, r.viable),
                  )}
                </div>
                <div className="w-28 shrink-0 text-right text-xs text-muted-foreground">
                  {r.viable
                    ? `${pct(r.positive, r.viable)} pos`
                    : "no viable"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SentimentBreakdownModal({
  team,
  sentiment,
  timeRange,
  rangeMode,
  customRange,
  agentId,
  onClose,
}: {
  team: AnalyticsTeamBreakdown;
  sentiment: SentimentKey;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  // Task #365 — forwarded so the per-agent breakdown honours the same
  // custom window the chart bar that opened this modal was scoped to.
  customRange?: CustomDateRange | null;
  agentId: string | null;
  onClose: () => void;
}) {
  // Fetch the per-agent breakdown for this single team, scoped to the
  // same filters the dashboard sent for the chart. A dedicated endpoint
  // (rather than deriving from `data.agents`) is required because an
  // agent on multiple teams must be attributed strictly to THIS team's
  // calls, otherwise the modal's totals could drift from the bar.
  const breakdownQ = useQuery<AnalyticsTeamAgentSentimentRow[]>({
    queryKey: [
      "team-sentiment-breakdown",
      team.team_id,
      timeRange,
      rangeMode,
      customRange?.start ?? null,
      customRange?.end ?? null,
      agentId,
    ],
    queryFn: () =>
      api.analyticsTeamSentimentBreakdown(
        team.team_id,
        timeRange,
        rangeMode,
        agentId,
        customRange,
      ),
    refetchOnWindowFocus: false,
  });
  const agents = useMemo(() => breakdownQ.data ?? [], [breakdownQ.data]);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      } else if (e.key === "Tab") {
        // Simple focus trap: keep focus within the dialog.
        const root = dialogRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    // Defer focus so the modal is mounted before we move it.
    queueMicrotask(() => closeBtnRef.current?.focus());
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Rows: each agent's count for the selected sentiment + their viable
  // count (used to render the per-agent percentage). Neutral is derived
  // as viable - positive - negative so it always matches the bar widths.
  const rows = useMemo(() => {
    const list = agents.map((a) => {
      const count =
        sentiment === "positive"
          ? a.positive
          : sentiment === "negative"
          ? a.negative
          : Math.max(0, a.viable - a.positive - a.negative);
      return {
        agent_id: a.agent_id,
        name: a.full_name || "(no name)",
        count,
        viable: a.viable,
      };
    });
    list.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [agents, sentiment]);

  const teamCount =
    sentiment === "positive"
      ? team.positive
      : sentiment === "negative"
      ? team.negative
      : Math.max(0, team.viable - team.positive - team.negative);
  const label = SENTIMENT_LABEL[sentiment];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-primary/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-lg rounded-lg bg-card shadow-lg"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3
            id={titleId}
            className="font-semibold text-foreground text-sm"
          >
            {team.team_name} — {label} calls
          </h3>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-muted-foreground hover:text-foreground text-sm  rounded"
          >
            ✕
          </button>
        </div>
        <div className="p-4">
          {breakdownQ.isLoading ? (
            <div
              role="status"
              aria-live="polite"
              className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground"
            >
              <Loader2
                className="h-4 w-4 animate-spin"
                aria-hidden
                strokeWidth={2.5}
              />
              <span>Loading breakdown…</span>
            </div>
          ) : breakdownQ.error ? (
            <div className="rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              Couldn't load the breakdown.{" "}
              <button
                type="button"
                className="underline hover:no-underline"
                onClick={() => breakdownQ.refetch()}
              >
                Retry
              </button>
            </div>
          ) : rows.length === 0 || teamCount === 0 ? (
            <div className="text-sm text-muted-foreground">
              No agents have {label.toLowerCase()} calls in this period.
            </div>
          ) : (
            <div className="overflow-hidden rounded border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Agent</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {label} calls
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      % of viable
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r) => (
                    <tr key={r.agent_id}>
                      <td className="px-3 py-2 text-foreground truncate">
                        {r.name}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {r.count.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {pct(r.count, r.viable)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/30">
                  <tr>
                    <td className="px-3 py-2 text-xs font-semibold text-muted-foreground">
                      Team total
                    </td>
                    <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums text-foreground">
                      {teamCount.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums text-muted-foreground">
                      {pct(teamCount, team.viable)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type SortKey =
  | "full_name"
  | "team_name"
  | "registered"
  | "viable"
  | "viable_pct"
  | "positive"
  | "quality"
  | "avg_duration_ms";

// Exported (currently unrendered) so it and its sortVal/FlagBadges helpers
// stay out of the unused-locals check without deleting the table implementation.
export function AgentTable({
  agents,
  onDrill,
}: {
  agents: AnalyticsAgentRow[];
  onDrill: (
    a: AnalyticsAgentRow,
    slice: AgentCallSlice | null,
  ) => void;
}) {
  const [sortKey, setSortKey] = useState<SortKey>("viable");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const sorted = useMemo(() => {
    const arr = [...agents];
    arr.sort((a, b) => {
      const va = sortVal(a, sortKey);
      const vb = sortVal(b, sortKey);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "string" && typeof vb === "string") {
        return dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return dir === "asc" ? (va as number) - (vb as number) : (vb as number) - (va as number);
    });
    return arr;
  }, [agents, sortKey, dir]);

  const onSort = (k: SortKey) => {
    if (k === sortKey) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      // Numeric columns default to descending (highest performers first);
      // text columns default to ascending alphabetic.
      setDir(k === "full_name" || k === "team_name" ? "asc" : "desc");
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">Agent breakdown</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-secondary/10 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th label="Agent"      k="full_name"       sortKey={sortKey} dir={dir} onSort={onSort} />
              <Th label="Team"       k="team_name"       sortKey={sortKey} dir={dir} onSort={onSort} />
              <Th label="Registered" k="registered"      sortKey={sortKey} dir={dir} onSort={onSort} align="right" metricId="registered" />
              <Th label="Viable"     k="viable"          sortKey={sortKey} dir={dir} onSort={onSort} align="right" metricId="viable" />
              <Th label="Viable %"   k="viable_pct"      sortKey={sortKey} dir={dir} onSort={onSort} align="right" metricId="viable_pct" />
              <Th label="Positive sentiment" k="positive" sortKey={sortKey} dir={dir} onSort={onSort} align="right" metricId="positive_sentiment" />
              <Th label="Quality calls" k="quality"      sortKey={sortKey} dir={dir} onSort={onSort} align="right" metricId="quality" />
              <Th label="Avg duration" k="avg_duration_ms" sortKey={sortKey} dir={dir} onSort={onSort} align="right" metricId="avg_duration" />
              <th className="px-3 py-2 text-left">Flags</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">
                  No agents in scope.
                </td>
              </tr>
            ) : (
              sorted.map((a) => {
                const canDrill = !!a.agent_id;
                const openRow = canDrill ? () => onDrill(a, null) : undefined;
                return (
                  <tr
                    key={a.agent_id}
                    onClick={openRow}
                    onKeyDown={(e) => {
                      // Only treat Enter/Space as a row activation when the
                      // event originated on the row itself — otherwise a
                      // keyboard user activating a flag-badge button would
                      // also bubble up here and override the slice filter
                      // with an unfiltered drill.
                      if (
                        openRow &&
                        e.target === e.currentTarget &&
                        (e.key === "Enter" || e.key === " ")
                      ) {
                        e.preventDefault();
                        openRow();
                      }
                    }}
                    tabIndex={openRow ? 0 : -1}
                    role={openRow ? "button" : undefined}
                    aria-label={
                      openRow
                        ? `Open ${a.full_name || "agent"} profile`
                        : undefined
                    }
                    className={`border-t border-border ${
                      openRow
                        ? "cursor-pointer hover:bg-primary/5 focus:bg-primary/5 focus:outline-none"
                        : ""
                    }`}
                  >
                    <td className="px-3 py-2 text-foreground">
                      {a.full_name || (
                        <span className="text-muted-foreground">Unknown agent</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{a.team_name || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{a.registered.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{a.viable.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pct(a.viable, a.registered)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {a.positive.toLocaleString()}
                      <span className="text-muted-foreground"> ({pct(a.positive, a.viable)})</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {a.quality.toLocaleString()}
                      <span className="text-muted-foreground"> ({pct(a.quality, a.viable)})</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {a.avg_duration_ms != null ? fmtDurationMs(a.avg_duration_ms) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <FlagBadges
                        flags={a.flags}
                        onDrill={
                          canDrill
                            ? (slice) => onDrill(a, slice)
                            : undefined
                        }
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function sortVal(a: AnalyticsAgentRow, k: SortKey): number | string | null {
  switch (k) {
    case "full_name": return a.full_name ?? "";
    case "team_name": return a.team_name ?? "";
    case "registered": return a.registered;
    case "viable": return a.viable;
    case "viable_pct": return pctNum(a.viable, a.registered);
    case "positive": return a.positive;
    case "quality": return a.quality;
    case "avg_duration_ms": return a.avg_duration_ms ?? -1;
  }
}

function Th({
  label, k, sortKey, dir, onSort, align = "left", metricId,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  dir: "asc" | "desc";
  onSort: (k: SortKey) => void;
  align?: "left" | "right";
  // Task #316 — when set, an info indicator is shown next to the
  // column label wired to the shared metric tooltip map. The sort
  // button still wraps the label; the indicator sits outside the
  // button so clicking it doesn't trigger a sort.
  metricId?: MetricId;
}) {
  const active = k === sortKey;
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className={`px-3 py-2 ${align === "right" ? "text-right" : "text-left"}`}>
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
        <button
          type="button"
          onClick={() => onSort(k)}
          className={`inline-flex items-center gap-1 ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
        >
          {label}
          <Icon className="h-3 w-3" aria-hidden />
        </button>
        {metricId ? (
          <LabelWithTooltip metricId={metricId} className="inline-flex items-center">
            {""}
          </LabelWithTooltip>
        ) : null}
      </span>
    </th>
  );
}

function FlagBadges({
  flags,
  onDrill,
}: {
  flags: AnalyticsAgentRow["flags"];
  onDrill?: (slice: AgentCallSlice | null) => void;
}) {
  type Item = {
    on: boolean;
    label: string;
    tip: string;
    tone: string;
    slice: AgentCallSlice | null;
  };
  const items: Item[] = (
    [
      {
        on: flags.speaking_too_fast,
        label: "Fast",
        tip: "Speaking too quickly on >35% of viable calls",
        tone: "bg-accent/20 text-accent-foreground border-accent/40",
        slice: "fast",
      },
      {
        on: flags.monologuing,
        label: "Mono",
        tip: "Monologuing on >50% of viable calls",
        tone: "bg-accent/20 text-accent-foreground border-accent/40",
        slice: "mono",
      },
      {
        // 0 negative is a *positive* signal — there's no underperforming
        // subset to inspect, so this badge stays informational and just
        // opens the unfiltered profile when clicked.
        on: flags.zero_negative,
        label: "0 neg",
        tip: "Zero negative-sentiment calls across ≥10 viable calls",
        tone: "bg-primary/15 text-primary border-primary/30",
        slice: null,
      },
    ] satisfies Item[]
  ).filter((f) => f.on);
  if (items.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="flex items-center gap-1">
      {items.map((f) =>
        onDrill ? (
          <Tooltip
            key={f.label}
            content={`${f.tip} — click to review the calls`}
          >
            <button
              type="button"
              onClick={(e) => {
                // Stop the row click from firing alongside the badge click —
                // it would override the slice we're trying to apply.
                e.stopPropagation();
                onDrill(f.slice);
              }}
              className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] hover:brightness-95 focus:outline-none focus:ring-1 focus:ring-ring ${f.tone}`}
            >
              <AlertTriangle className="h-2.5 w-2.5" aria-hidden />
              {f.label}
            </button>
          </Tooltip>
        ) : (
          <span
            key={f.label}
            title={f.tip}
            className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] ${f.tone}`}
          >
            <AlertTriangle className="h-2.5 w-2.5" aria-hidden />
            {f.label}
          </span>
        ),
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ticket 12 — Team Assessments section (supadmin-only)
// ---------------------------------------------------------------------------

function AgentListPanel({
  agents,
  teamId,
  timeRange,
  rangeMode,
  onAgentDrillNav: _onAgentDrillNav,
}: {
  agents: AnalyticsTeamAgent[];
  teamId: string;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  // Task #391 — agent rows now render as new-tab anchors, so the
  // legacy click handler is no longer invoked here. The prop is kept
  // on the type to avoid churning callers/tests.
  onAgentDrillNav: AgentDrillNav;
}) {
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
        Agents on this team
      </div>
      {agents.length === 0 ? (
        <div className="text-sm text-muted-foreground">—</div>
      ) : (
        agents.map((a) => {
          const slug = agentSlug({
            full_name: a.agent_name,
            email: null,
            agent_id: a.agent_id,
          });
          const label = a.agent_name ?? "Unknown agent";
          // Task #391 — render a new-tab anchor instead of a click
          // handler so the dashboard tab keeps its place.
          const href = agentProfileHref(slug, a.agent_id, {
            slice: null,
            team: teamId,
            timeRange,
            rangeMode,
          });
          return (
            <a
              key={a.agent_id}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded hover:bg-muted/40 transition-colors text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="text-sm text-foreground truncate">{label}</span>
              <ArrowRight
                className="h-4 w-4 text-muted-foreground shrink-0"
                aria-hidden="true"
              />
            </a>
          );
        })
      )}
    </div>
  );
}

// Task #290 + Task #312 — the focused agent panel for a
// TeamAssessmentCard now lives in the shared
// ``FocusedMeasureAgentPanel`` so the By team and By assessment
// surfaces render the same agents-first drilldown.

export function TeamAssessmentCard({
  team,
  rangeFrom,
  rangeTo,
  timeRange,
  rangeMode,
  onAgentDrillNav,
  viewerRole,
  assessmentId = null,
  agentId = null,
}: {
  team: AnalyticsTeamOption;
  rangeFrom: string | null;
  rangeTo: string | null;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  onAgentDrillNav: AgentDrillNav;
  // Supadmin sees the extra "Agents on this team" drill panel; admins
  // and managers see only the summary + headline stats + detection
  // table (the agent list relies on supadmin-only endpoints).
  viewerRole: Role;
  // Optional filter — when set, the team aggregate is requeried with
  // this assessment_id so the headline stats and detection table show
  // only that assessment's rows. ``null`` means "All assessments".
  assessmentId?: string | null;
  // Task #302 — global Agent filter from the Teams view page
  // header. When set every metric on this card (aggregate, stacked
  // bar, trend line) is scoped to that agent's calls.
  agentId?: string | null;
}) {
  const showAgentPanel = viewerRole === "supadmin";

  // Task #226 — derive a prior-period window of equal length so the
  // single-team Assessment performance card can render the same
  // "↑/↓ pts vs prior" chip + per-bar prior markers the Agent
  // Profile card uses (Task #220). ``all_time`` has no defined
  // prior so we pass null through and the comparison is omitted —
  // mirrors the triage-list / agent-profile pattern.
  const { priorFrom, priorTo } = useMemo<{
    priorFrom: string | null;
    priorTo: string | null;
  }>(() => {
    if (!rangeFrom || !rangeTo || timeRange === "all_time") {
      return { priorFrom: null, priorTo: null };
    }
    const fromMs = Date.parse(rangeFrom);
    const toMs = Date.parse(rangeTo);
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) {
      return { priorFrom: null, priorTo: null };
    }
    const windowMs = toMs - fromMs;
    return {
      priorFrom: new Date(fromMs - windowMs).toISOString(),
      priorTo: new Date(fromMs).toISOString(),
    };
  }, [rangeFrom, rangeTo, timeRange]);

  const aggQ = useQuery<TeamAggregate>({
    queryKey: [
      "assessment-aggregate-team",
      team.id,
      rangeFrom,
      rangeTo,
      priorFrom,
      priorTo,
      assessmentId,
      agentId,
    ],
    queryFn: () =>
      api.aggregateForTeam(team.id, {
        from: rangeFrom!,
        to: rangeTo!,
        prior_from: priorFrom,
        prior_to: priorTo,
        ...(assessmentId ? { assessment_id: assessmentId } : {}),
        ...(agentId ? { agent_id: agentId } : {}),
      }),
    enabled: !!rangeFrom && !!rangeTo,
  });

  // Task #171 — by-assessment aggregates power the per-team stacked
  // bar in "By team" mode. Same queryKey as ``AssessmentCardsList`` so
  // React Query dedupes the fetch when both surfaces are mounted.
  const byAssessmentQ = useQuery<AssessmentAggregate[]>({
    queryKey: ["by-assessment-aggregates", rangeFrom, rangeTo, agentId],
    queryFn: () =>
      api.listByAssessmentAggregates({
        from: rangeFrom!,
        to: rangeTo!,
        agent_id: agentId,
      }),
    enabled: !!rangeFrom && !!rangeTo,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });

  const agentsQ = useQuery<AnalyticsTeamAgent[]>({
    queryKey: ["team-agents", team.id],
    queryFn: () => api.listTeamAgents(team.id),
    // Only supadmin renders the agent panel and listTeamAgents is
    // gated to supadmin on the backend — don't even fire the request
    // for admins / managers.
    enabled: showAgentPanel,
  });

  // Task #290 + Task #312 — clicking a bar in the MeasureBarChart
  // focuses that (measure, label) pair. While a focus is active the
  // supadmin agent list is replaced with the shared
  // ``FocusedMeasureAgentPanel``. Cleared on range change so a
  // measure that disappears from the new period can't strand the
  // panel in a stale empty state.
  const [focused, setFocused] = useState<MeasureFocus | null>(null);
  useEffect(() => {
    setFocused(null);
  }, [rangeFrom, rangeTo, assessmentId]);

  return (
    <CollapsibleSection
      title={
        <h4 className="text-sm font-semibold text-foreground">{team.name}</h4>
      }
      rightAdornment={
        <div className="text-xs text-muted-foreground">
          {rangeTitle(timeRange, rangeMode)}
        </div>
      }
      storageKey={`intelligence:team-card:${team.id}`}
      forceOpen={!!assessmentId}
      bodyClassName="p-4 space-y-4 border-t border-border"
    >
      <>
        {aggQ.isLoading ? (
          <div
            className="bg-muted/40 rounded h-16 animate-pulse"
            aria-hidden="true"
          />
        ) : aggQ.error ? (
          <div className="text-sm text-destructive flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              {(aggQ.error as { message?: string })?.message ||
                "Failed to load aggregate."}
            </span>
            <button
              type="button"
              onClick={() => aggQ.refetch()}
              className="ml-auto inline-flex items-center gap-1 rounded border border-input bg-card px-2 py-0.5 text-xs hover:bg-muted/40"
            >
              Retry
            </button>
          </div>
        ) : aggQ.data ? (
          aggQ.data.call_count === 0 || aggQ.data.per_label.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No assessments graded in this period.
            </p>
          ) : (
            (() => {
              // Task #226 — mirror the Agent Profile card: summarise
              // current + prior period, derive a points-pp delta for
              // the trend chip, and pass ``priorRows`` through to the
              // MeasureBarChart so each bar carries a prior-period
              // tick. ``priorSummary`` is null when there is no usable
              // prior window (e.g. All-time, or the prior period had
              // no graded calls) — the chip falls back to an em-dash.
              const summary = summariseMeasures(aggQ.data.per_label);
              const priorPerLabel = aggQ.data.prior_per_label ?? [];
              const priorCallCount = aggQ.data.prior_call_count ?? 0;
              const hasPriorWindow = !!(priorFrom && priorTo);
              const priorSummary =
                hasPriorWindow && priorCallCount > 0
                  ? summariseMeasures(priorPerLabel)
                  : null;
              const trendDelta =
                summary && priorSummary
                  ? summary.overallPct - priorSummary.overallPct
                  : null;
              return (
                <>
                  {summary ? (
                    <AssessmentSummaryStrip
                      summary={summary}
                      inScopeCallCount={aggQ.data.call_count}
                      scopeLabel="team"
                      trendDelta={trendDelta}
                      hasPriorWindow={hasPriorWindow}
                      perLabel={aggQ.data.per_label}
                      priorPerLabel={priorPerLabel}
                      deltaPanelTitle={`What changed — ${team.name}`}
                      deltaPanelSubtitle={`${rangeTitle(timeRange, rangeMode)} vs prior period`}
                    />
                  ) : null}
                  <CallCountCaption count={aggQ.data.call_count} />
                  {/* Task #171 — per-team charts at the top of each
                      team card in "By team" mode: stacked bar split
                      by assessment + daily trend across all
                      assessments. */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <TeamCallsByAssessmentStackedBar
                      teamId={team.id}
                      teamName={team.name}
                      aggregates={byAssessmentQ.data}
                      isLoading={byAssessmentQ.isLoading}
                      error={
                        byAssessmentQ.error
                          ? (byAssessmentQ.error as { message?: string })
                          : null
                      }
                      onRetry={() => byAssessmentQ.refetch()}
                    />
                    <TeamTrendLineChart
                      teamId={team.id}
                      assessmentId={assessmentId}
                      rangeFrom={rangeFrom}
                      rangeTo={rangeTo}
                      agentId={agentId}
                    />
                  </div>
                  <MeasureBarChart
                    rows={aggQ.data.per_label}
                    callCount={aggQ.data.call_count}
                    priorRows={hasPriorWindow ? priorPerLabel : undefined}
                    focused={focused}
                    onFocus={setFocused}
                    groupStorageKey={`intelligence:team:${team.id}:assessment:${assessmentId ?? "any"}`}
                  />
                  <TeamHeadlineStats
                    callCount={aggQ.data.call_count}
                    perLabel={aggQ.data.per_label}
                  />
                </>
              );
            })()
          )
        ) : null}
        {showAgentPanel ? (
          focused ? (
            <FocusedMeasureAgentPanel
              teamId={team.id}
              focused={focused}
              rangeFrom={rangeFrom}
              rangeTo={rangeTo}
              priorFrom={priorFrom}
              priorTo={priorTo}
              timeRange={timeRange}
              rangeMode={rangeMode}
              assessmentId={assessmentId}
              agentScopeId={agentId}
              onAgentDrillNav={onAgentDrillNav}
              onClear={() => setFocused(null)}
            />
          ) : (
            <AgentListPanel
              agents={agentsQ.data ?? []}
              teamId={team.id}
              timeRange={timeRange}
              rangeMode={rangeMode}
              onAgentDrillNav={onAgentDrillNav}
            />
          )
        ) : null}
      </>
    </CollapsibleSection>
  );
}

// Two compact headline stats that sit above the per-label detection
// table on each TeamAssessmentCard: total calls assessed in the
// period, and the call-weighted overall detection rate across every
// measure/label row. Renders nothing when the period has no calls so
// we never show "0 calls · 0%" alongside the no-data summary copy.
function TeamHeadlineStats({
  callCount,
  perLabel,
}: {
  callCount: number;
  perLabel: { detection_rate: number; calls: number }[];
}) {
  if (!callCount) return null;
  const weight = perLabel.reduce((sum, r) => sum + r.calls, 0);
  const weighted = perLabel.reduce(
    (sum, r) => sum + r.detection_rate * r.calls,
    0,
  );
  const overall = weight > 0 ? weighted / weight : 0;
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Calls assessed
        </div>
        <div className="mt-0.5 text-lg font-semibold text-foreground">
          {callCount.toLocaleString()}
        </div>
      </div>
      <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Overall detection rate
        </div>
        <div className="mt-0.5 text-lg font-semibold text-foreground">
          {`${Math.round(overall * 100)}%`}
        </div>
      </div>
    </div>
  );
}

export function TeamAssessmentsSection({
  selectedTeamId,
  selectedAgentId = null,
  rangeFrom,
  rangeTo,
  timeRange,
  rangeMode,
  onAgentDrillNav,
  onOpenCall,
}: {
  // Optional so legacy callers / tests that still pass ``teams`` don't
  // need updating; the section's by-assessment surface gets its team
  // rows from the aggregates endpoint, not this prop.
  teams?: AnalyticsTeamOption[];
  selectedTeamId: string;
  // Task #302 — global Agent filter from the Teams view page
  // header. ``null`` (or empty) ⇒ "All agents".
  selectedAgentId?: string | null;
  rangeFrom: string | null;
  rangeTo: string | null;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  onAgentDrillNav: AgentDrillNav;
  // Kept optional for back-compat with existing callers / tests. The
  // section itself no longer needs role-gated chrome.
  viewerRole?: Role;
  // Task #286 — opens the call detail view from the focused calls
  // list under each team in the "By assessment" view. Optional so
  // existing test consumers don't need to wire it.
  onOpenCall?: (callId: string) => void;
}) {
  // Task #211 — derive a prior-period window of equal length so each
  // per-team row in the by-assessment cards can render the same
  // prior-period tick the by-team triage list previously showed.
  // All-time has no defined prior so we pass null through and the
  // comparison is omitted.
  const { priorFrom, priorTo } = useMemo<{
    priorFrom: string | null;
    priorTo: string | null;
  }>(() => {
    if (!rangeFrom || !rangeTo || timeRange === "all_time") {
      return { priorFrom: null, priorTo: null };
    }
    const fromMs = Date.parse(rangeFrom);
    const toMs = Date.parse(rangeTo);
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) {
      return { priorFrom: null, priorTo: null };
    }
    const windowMs = toMs - fromMs;
    return {
      priorFrom: new Date(fromMs - windowMs).toISOString(),
      priorTo: new Date(fromMs).toISOString(),
    };
  }, [rangeFrom, rangeTo, timeRange]);
  return (
    <section className="rounded-lg border border-border bg-card shadow-md animate-card-enter">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-foreground">
          Team performance
        </h3>
      </div>
      <div className="p-4 space-y-4">
        <AssessmentCardsList
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          priorFrom={priorFrom}
          priorTo={priorTo}
          timeRange={timeRange}
          rangeMode={rangeMode}
          teamId={selectedTeamId || null}
          agentId={selectedAgentId || null}
          onAgentDrillNav={onAgentDrillNav}
          onOpenCall={onOpenCall}
        />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Ticket 18 — By-assessment mode
// ---------------------------------------------------------------------------
//
// Mirrors the team-mode card stack: one card per assessment in scope,
// each driving an inline three-step drill (assessment chart → team rows →
// agent buttons). The list query runs once per (from, to) and reuses
// keepPreviousData so flipping modes (or shifting the range) keeps the
// last good render until the next response lands instead of flashing a
// blank skeleton.

function AssessmentCardsList({
  rangeFrom,
  rangeTo,
  priorFrom,
  priorTo,
  timeRange,
  rangeMode,
  teamId = null,
  agentId = null,
  onAgentDrillNav,
  onOpenCall,
}: {
  rangeFrom: string | null;
  rangeTo: string | null;
  // Task #211 — paired prior window so each per-team row in the
  // by-assessment cards can render the same prior-period tick the
  // by-team triage list already shows. Both null on All-time.
  priorFrom: string | null;
  priorTo: string | null;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  // Task #302 — global Team + Agent filters from the Teams
  // View page header. ``null`` means "All teams" / "All agents".
  // Scoped here rather than client-side so the backend can drop
  // assessments that fall outside the active scope.
  teamId?: string | null;
  agentId?: string | null;
  onAgentDrillNav: AgentDrillNav;
  // Task #286 — opens the per-call detail view from the focused calls
  // list that surfaces when a measure bar in a team row is clicked.
  // Optional so existing test consumers don't have to wire it.
  onOpenCall?: (callId: string) => void;
}) {
  const q = useQuery<AssessmentAggregate[]>({
    queryKey: [
      "by-assessment-aggregates",
      rangeFrom,
      rangeTo,
      priorFrom,
      priorTo,
      teamId,
      agentId,
    ],
    queryFn: () =>
      api.listByAssessmentAggregates({
        from: rangeFrom!,
        to: rangeTo!,
        prior_from: priorFrom,
        prior_to: priorTo,
        team_id: teamId,
        agent_id: agentId,
      }),
    enabled: !!rangeFrom && !!rangeTo,
    // Match the existing aggregate queries' "no flash on mode switch"
    // behaviour — the global QueryClient already sets keepPreviousData
    // via placeholderData, so just keep the prior data visible on
    // refetch instead of unmounting to skeletons.
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });

  if (q.isLoading && !q.data) {
    return (
      <div
        className="bg-muted/40 rounded h-24 animate-pulse"
        aria-hidden="true"
      />
    );
  }
  if (q.error && !q.data) {
    return (
      <div className="text-sm text-destructive flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          {(q.error as { message?: string })?.message ||
            "Failed to load assessments."}
        </span>
        <button
          type="button"
          onClick={() => q.refetch()}
          className="ml-auto inline-flex items-center gap-1 rounded border border-input bg-card px-2 py-0.5 text-xs hover:bg-muted/40"
        >
          Retry
        </button>
      </div>
    );
  }
  const data = q.data ?? [];
  if (data.length === 0) {
    // Task #188 — keep the section's card layout stable across ranges
    // (Daily often has zero graded calls early in the day). Render an
    // empty-state card with the same outer styling and an approximate
    // height equivalent to a populated ``AssessmentCard`` so switching
    // between Daily and Weekly doesn't reshape the page.
    return (
      <div className="space-y-3">
        <section className="rounded-lg border border-border bg-card shadow-md animate-card-enter">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <h4 className="text-sm font-semibold text-foreground truncate">
                Assessments
              </h4>
            </div>
            <div className="text-xs text-muted-foreground">
              {rangeTitle(timeRange, rangeMode)}
            </div>
          </div>
          <div
            className="p-4 flex items-center justify-center text-sm text-muted-foreground italic"
            style={{ minHeight: 320 }}
          >
            No assessments graded in this period.
          </div>
        </section>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {data.map((a) => (
        <AssessmentCard
          key={a.assessment_id}
          agg={a}
          timeRange={timeRange}
          rangeMode={rangeMode}
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          teamId={teamId}
          agentId={agentId}
          onAgentDrillNav={onAgentDrillNav}
          onOpenCall={onOpenCall}
        />
      ))}
    </div>
  );
}

function AssessmentCard({
  agg,
  timeRange,
  rangeMode,
  rangeFrom,
  rangeTo,
  agentId = null,
  onAgentDrillNav,
  onOpenCall,
}: {
  agg: AssessmentAggregate;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  rangeFrom: string | null;
  rangeTo: string | null;
  // Task #302 — global Team + Agent filters forwarded to the
  // per-assessment trend chart so the daily line reflects the same
  // scope as the parent card's headline numbers.
  teamId?: string | null;
  agentId?: string | null;
  onAgentDrillNav: AgentDrillNav;
  // Task #286 — optional so the existing test harness (which doesn't
  // exercise the focused calls list) doesn't have to wire it.
  onOpenCall?: (callId: string) => void;
}) {
  // Each team row keeps its own open/closed flag. A Set keeps the toggle
  // O(1) and supports multiple simultaneously expanded teams. Transient
  // UI, so it intentionally does not round-trip through the URL.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggle = (teamId: string) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  };
  const teamCount = agg.teams.length;
  return (
    <CollapsibleSection
      title={
        <div className="flex items-center gap-2 min-w-0">
          <h4 className="text-sm font-semibold text-foreground truncate">
            {agg.assessment_name}
          </h4>
          <span className="inline-flex rounded-full bg-secondary/15 text-secondary border border-secondary/30 px-2 py-0.5 text-xs font-medium ml-2">
            v{agg.assessment_version}
          </span>
        </div>
      }
      rightAdornment={
        <div className="text-xs text-muted-foreground">
          {rangeTitle(timeRange, rangeMode)}
        </div>
      }
      storageKey={`intelligence:assessment-card:${agg.assessment_id}`}
      defaultOpen={false}
      bodyClassName="p-4 space-y-4 border-t border-border"
    >
      <>
        <CallCountCaption count={agg.call_count} />
        {teamCount === 0 ? (
          <div className="text-sm text-muted-foreground italic">
            No in-scope teams for this assessment.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Teams ({teamCount})
            </div>
            <div className="space-y-1">
              {(() => {
                // Task #197 — apply the same triage sort + severity
                // signal here so "By assessment" mode matches "By
                // team" mode. The server's by-assessment payload has
                // no prior period so prior_per_label is empty; the
                // tier still flags low absolute rates and worstMeasure
                // falls back to the lowest detection rate.
                const cohortAvg = (() => {
                  const weight = agg.per_label.reduce(
                    (s, r) => s + r.calls,
                    0,
                  );
                  if (weight <= 0) return 0;
                  return (
                    agg.per_label.reduce(
                      (s, r) => s + r.detection_rate * r.calls,
                      0,
                    ) / weight
                  );
                })();
                // Task #211 — feed the prior-period overlay through
                // to triageSort + the expanded MeasureBarChart so
                // "By assessment" rows pick up the same delta tick
                // "By team" mode shows.
                const teamAvgPerLabel = cohortAveragePerLabel(
                  agg.teams.map((t) => ({
                    team_id: t.team_id,
                    team_name: t.team_name,
                    call_count: t.call_count,
                    per_label: t.per_label,
                    prior_call_count: t.prior_call_count ?? 0,
                    prior_per_label: t.prior_per_label ?? [],
                  })),
                );
                const teamAvgRows: import("@/lib/api").AggregateRow[] = [];
                const seen = new Set<string>();
                for (const t of agg.teams) {
                  for (const r of t.per_label) {
                    const key = `${r.measure_id}::${r.label_name}`;
                    if (seen.has(key)) continue;
                    seen.add(key);
                    teamAvgRows.push({
                      measure_id: r.measure_id,
                      measure_label: r.measure_label,
                      label_name: r.label_name,
                      detection_rate: teamAvgPerLabel.get(key) ?? 0,
                      calls: 0,
                    });
                  }
                }
                const sorted = triageSort(
                  agg.teams.map((t) => ({
                    team_id: t.team_id,
                    team_name: t.team_name,
                    call_count: t.call_count,
                    per_label: t.per_label,
                    prior_call_count: t.prior_call_count ?? 0,
                    prior_per_label: t.prior_per_label ?? [],
                  })),
                );
                return sorted.map((tRow) => {
                  const team = agg.teams.find(
                    (t) => t.team_id === tRow.team_id,
                  )!;
                  const tier = severityTier(tRow, cohortAvg);
                  const overall = overallDetectionRate(team.per_label);
                  return (
                    <TeamDrillRow
                      key={team.team_id}
                      team={team}
                      teamAvgRows={teamAvgRows}
                      priorRows={team.prior_per_label}
                      tier={tier}
                      overall={overall}
                      agents={agg.agents.filter(
                        (a) => a.team_id === team.team_id,
                      )}
                      expanded={expanded.has(team.team_id)}
                      onToggle={() => toggle(team.team_id)}
                      timeRange={timeRange}
                      rangeMode={rangeMode}
                      onAgentDrillNav={onAgentDrillNav}
                      assessmentId={agg.assessment_id}
                      rangeFrom={rangeFrom}
                      rangeTo={rangeTo}
                      onOpenCall={onOpenCall}
                      agentScopeId={agentId}
                    />
                  );
                });
              })()}
            </div>
          </div>
        )}
      </>
    </CollapsibleSection>
  );
}

function TeamDrillRow({
  team,
  agents,
  expanded,
  onToggle,
  timeRange,
  rangeMode,
  onAgentDrillNav,
  teamAvgRows,
  priorRows,
  tier,
  overall,
  assessmentId,
  rangeFrom,
  rangeTo,
  onOpenCall: _onOpenCall,
  agentScopeId = null,
}: {
  team: import("@/lib/api").AssessmentAggregateTeamRow;
  agents: import("@/lib/api").AssessmentAggregateAgentRow[];
  expanded: boolean;
  onToggle: () => void;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  onAgentDrillNav: AgentDrillNav;
  // Task #197 — optional triage props. When omitted (older callers /
  // tests) the row renders exactly like before so this remains a
  // backwards-compatible enhancement.
  teamAvgRows?: import("@/lib/api").AggregateRow[];
  // Task #211 — prior-period overlay for the expanded MeasureBarChart
  // so by-assessment cards show the same prior tick as by-team mode.
  priorRows?: import("@/lib/api").AggregateRow[];
  tier?: import("@/lib/triage").SeverityTier;
  overall?: number;
  // Task #286 — optional props that wire the team-level measure chart
  // up to the focused agent panel. Omitted by the older test harness,
  // in which case the chart stays non-interactive exactly as before.
  // Task #312 — onOpenCall is accepted for back-compat but no longer
  // used: clicking a measure bar opens an agents-first drilldown.
  assessmentId?: string;
  rangeFrom?: string | null;
  rangeTo?: string | null;
  onOpenCall?: (callId: string) => void;
  // Task #312 — global agent filter, forwarded so the focused panel
  // shows only that agent when one is selected at the page level.
  agentScopeId?: string | null;
}) {
  // Task #286 — focused (measure, label) state lives here so collapsing
  // and re-expanding the same team row starts with a clean slate.
  // Task #312 — the per-row Absent/Present sub-filter is now owned by
  // the shared FocusedMeasureAgentPanel; this row only tracks which
  // measure is focused.
  const [focused, setFocused] = useState<MeasureFocus | null>(null);
  useEffect(() => {
    if (!expanded) setFocused(null);
  }, [expanded]);
  // Task #312 — measure-bar clicks now open the agents-first
  // drilldown, so the chart becomes interactive whenever we have a
  // token + assessmentId (onOpenCall is no longer required).
  const focusInteractive = !!assessmentId;
  // Task #312 — prior-period window mirrors TeamAssessmentCard's
  // computation so the focused panel's Δpp chip works in
  // "By assessment" mode too.
  const { priorFrom, priorTo } = useMemo<{
    priorFrom: string | null;
    priorTo: string | null;
  }>(() => {
    if (!rangeFrom || !rangeTo) {
      return { priorFrom: null, priorTo: null };
    }
    const fromMs = new Date(rangeFrom).getTime();
    const toMs = new Date(rangeTo).getTime();
    const windowMs = toMs - fromMs;
    if (!Number.isFinite(windowMs) || windowMs <= 0) {
      return { priorFrom: null, priorTo: null };
    }
    return {
      priorFrom: new Date(fromMs - windowMs).toISOString(),
      priorTo: new Date(fromMs).toISOString(),
    };
  }, [rangeFrom, rangeTo]);
  const callLabel = team.call_count === 1 ? "call" : "calls";
  // Task #223 — mirrors the by-team triage row: low-sample rows mute
  // their numbers and append a caveat onto the severity / call-count
  // tooltips so users don't trust the percentages too far.
  const lowSample = isLowSample(team.call_count);
  const SEVERITY_DOT: Record<
    import("@/lib/triage").SeverityTier,
    string
  > = {
    red: "bg-destructive",
    amber: "bg-yellow-500",
    green: "bg-emerald-500",
    neutral: "bg-muted-foreground/40",
  };
  return (
    <div
      className="rounded border border-border bg-card"
      data-testid="assessment-team-drill-row"
      data-team-id={team.team_id}
    >
      {/* Task #223 — div + role=button mirrors TeamTriageList so we
          can nest focusable spans (Tooltip triggers) without invalid
          HTML inside a real <button>. Keyboard activation matches a
          native button (Enter / Space toggles), and the inner
          tooltip segments stopPropagation so activating them doesn't
          collapse the row. */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            if (e.target !== e.currentTarget) return;
            e.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] cursor-pointer"
      >
        <span className="flex items-center gap-2 min-w-0">
          <ChevronRight
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-[var(--duration-base)] ease-[var(--easing-standard)] ${
              expanded ? "rotate-90" : ""
            }`}
            aria-hidden="true"
          />
          {tier ? (
            <Tooltip
              content={
                lowSample
                  ? `${TRIAGE_TOOLTIP_COPY.severity[tier]} ${LOW_SAMPLE_TOOLTIP}`
                  : TRIAGE_TOOLTIP_COPY.severity[tier]
              }
              data-testid="assessment-team-tooltip-severity"
            >
              <span
                tabIndex={0}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") e.stopPropagation();
                }}
                className="inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
              >
                <span
                  role="img"
                  aria-label={`Severity ${tier} for ${team.team_name}`}
                  data-severity={tier}
                  className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ${SEVERITY_DOT[tier]}`}
                />
              </span>
            </Tooltip>
          ) : null}
          <span className="text-sm text-foreground truncate">
            {team.team_name}
          </span>
        </span>
        {team.call_count === 0 ? (
          <Tooltip
            content={TRIAGE_TOOLTIP_COPY.noGradedCalls}
            data-testid="assessment-team-tooltip-no-graded"
          >
            <span
              tabIndex={0}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") e.stopPropagation();
              }}
              className="text-xs text-muted-foreground italic tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              No graded calls
            </span>
          </Tooltip>
        ) : (
          <span
            className={`text-xs tabular-nums inline-flex items-center gap-1 ${
              lowSample ? "text-muted-foreground/60" : "text-muted-foreground"
            }`}
            data-low-sample={lowSample ? "true" : undefined}
          >
            {overall !== undefined ? (
              <>
                <Tooltip
                  content={TRIAGE_TOOLTIP_COPY.overall}
                  data-testid="assessment-team-tooltip-overall"
                >
                  <span
                    tabIndex={0}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        e.stopPropagation();
                    }}
                    className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  >
                    Overall {Math.round(overall * 100)}%
                  </span>
                </Tooltip>
                <span aria-hidden="true">·</span>
              </>
            ) : null}
            <Tooltip
              content={
                lowSample
                  ? `${TRIAGE_TOOLTIP_COPY.callCount} ${LOW_SAMPLE_TOOLTIP}`
                  : TRIAGE_TOOLTIP_COPY.callCount
              }
              data-testid="assessment-team-tooltip-calls"
            >
              <span
                tabIndex={0}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") e.stopPropagation();
                }}
                className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              >
                {team.call_count.toLocaleString()} {callLabel}
              </span>
            </Tooltip>
            {lowSample ? (
              <Tooltip
                content={LOW_SAMPLE_TOOLTIP}
                data-testid="assessment-team-tooltip-low-sample"
              >
                <span
                  tabIndex={0}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      e.stopPropagation();
                  }}
                  className="text-[10px] font-medium uppercase tracking-wide rounded border border-border bg-muted/40 text-muted-foreground px-1.5 py-0.5 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="assessment-team-low-sample-chip"
                >
                  Low sample
                </span>
              </Tooltip>
            ) : null}
          </span>
        )}
      </div>
      {expanded ? (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-border">
          <MeasureBarChart
            rows={team.per_label}
            callCount={team.call_count}
            priorRows={priorRows}
            teamAverageRows={teamAvgRows}
            focused={focusInteractive ? focused : undefined}
            onFocus={focusInteractive ? (f) => setFocused(f) : undefined}
            groupStorageKey={`intelligence:by-assessment:${assessmentId ?? "any"}:team:${team.team_id}`}
          />
          {focusInteractive && focused ? (
            <FocusedMeasureAgentPanel
              teamId={team.team_id}
              focused={focused}
              rangeFrom={rangeFrom ?? null}
              rangeTo={rangeTo ?? null}
              priorFrom={priorFrom}
              priorTo={priorTo}
              timeRange={timeRange}
              rangeMode={rangeMode}
              assessmentId={assessmentId!}
              agentScopeId={agentScopeId}
              onAgentDrillNav={onAgentDrillNav}
              onClear={() => setFocused(null)}
            />
          ) : null}
          {agents.length > 0 && !focused ? (
            <div className="space-y-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Agents ({agents.length})
              </div>
              <div className="space-y-1">
                {agents.map((agent) => {
                  const label = agent.agent_name ?? "—";
                  const slug = agentSlug({
                    full_name: agent.agent_name,
                    email: null,
                    agent_id: agent.agent_id,
                  });
                  const aLabel =
                    agent.call_count === 1 ? "call" : "calls";
                  // Task #391 — render a new-tab anchor instead of a
                  // click handler so the dashboard tab keeps its place.
                  const href = agentProfileHref(slug, agent.agent_id, {
                    slice: null,
                    team: agent.team_id,
                    timeRange,
                    rangeMode,
                  });
                  return (
                    <a
                      key={agent.agent_id}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-between gap-3 rounded px-3 py-2 text-left hover:bg-primary/5  transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="text-sm text-foreground truncate">
                        {label}
                      </span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {agent.call_count.toLocaleString()} {aLabel}
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function TeamsDashboardView({
  me,
  onSelectAgent,
  onOpenCall,
}: {
  me: UserOut;
  onSelectAgent: AgentDrillNav;
  // Task #286 — open the call-detail view from the focused calls list
  // that appears under a team when a measure bar is clicked in the
  // "By assessment" view. Optional so existing test consumers don't
  // need to wire it.
  onOpenCall?: (callId: string) => void;
}) {
  const isManager = me.role === "manager";
  const initUrl = useMemo(() => parseUrl(), []);
  const [teamId, setTeamId] = useState<string>("");
  const [agentId, setAgentId] = useState<string>("");
  const {
    timeRange, rangeMode, customRange, effectiveRangeMode,
    onTimeRangeChange, onRangeModeChange, onCustomRangeChange,
  } = useTimeRange({
    initial: {
      // PET-667 — default the Teams view to "Week" when the URL carries
      // no range. A deep link (?tr=…) still wins: initUrl.timeRange is
      // only null when no range was specified.
      timeRange: initUrl.timeRange ?? "weekly",
      rangeMode: initUrl.rangeMode,
      customRange:
        initUrl.customStart && initUrl.customEnd
          ? { start: initUrl.customStart, end: initUrl.customEnd }
          : null,
    },
    sync: "url",
    urlNavKey: "intelligence",
  });
  const [compare, setCompare] = useState<boolean>(false);
  const [funnelFilter, setFunnelFilter] = useState<FunnelFilter>(null);
  const [sentimentFilter, setSentimentFilter] = useState<SentimentFilter>(null);
  const [sentimentDrill, setSentimentDrill] = useState<{
    team: AnalyticsTeamBreakdown;
    sentiment: SentimentKey;
    trigger: HTMLElement | null;
  } | null>(null);
  const toggleFunnel = (key: Exclude<FunnelFilter, null>) =>
    setFunnelFilter((cur) => (cur === key ? null : key));
  const toggleSentiment = (key: Exclude<SentimentFilter, null>) =>
    setSentimentFilter((cur) => (cur === key ? null : key));

  const effectiveCompare = compare && timeRange !== "all_time";

  const onSelectAgentWithRange: AgentDrillNav = (slug, key, opts) =>
    onSelectAgent(slug, key, {
      ...opts,
      timeRange: opts.timeRange ?? timeRange,
      rangeMode: opts.rangeMode ?? rangeMode,
      customStart: opts.customStart ?? customRange?.start ?? null,
      customEnd: opts.customEnd ?? customRange?.end ?? null,
    });

  // Managers must never see cross-team data, so we resolve the manager's
  // assigned teams from a lightweight scope fetch *before* the first
  // analytics request. For a manager with a single team this lets us
  // auto-select it (avoiding a flicker where an unscoped request lands
  // first); for a manager with multiple teams we leave the selection
  // empty so the dropdown can offer an "All teams" aggregate.
  const scopeQ = useQuery({
    queryKey: ["analytics-scope"],
    queryFn: () => api.analyticsDashboard(null),
    enabled: isManager && !teamId,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });
  const managerTeamCount = scopeQ.data?.scope.team_options.length ?? 0;
  const managerAllTeams = isManager && managerTeamCount > 1;
  useEffect(() => {
    if (!isManager || teamId) return;
    const opts = scopeQ.data?.scope.team_options;
    if (!opts) return;
    // Single-team manager: auto-pick that team to preserve today's
    // behaviour. Multi-team managers stay on the "All teams" default.
    const onlyTeam = opts.length === 1 ? opts[0] : undefined;
    if (onlyTeam) setTeamId(onlyTeam.id);
  }, [isManager, teamId, scopeQ.data]);

  const q = useQuery<AnalyticsDashboard>({
    queryKey: [
      "analytics-dashboard",
      teamId || null,
      agentId || null,
      timeRange,
      effectiveCompare,
      effectiveRangeMode,
      customRange?.start ?? null,
      customRange?.end ?? null,
    ],
    queryFn: () =>
      api.analyticsDashboard(
        teamId || null,
        timeRange,
        effectiveCompare,
        agentId || null,
        effectiveRangeMode,
        customRange,
      ),
    // For managers, defer the real fetch until we either have a concrete
    // team or have confirmed (via scopeQ) that the manager owns multiple
    // teams and the "All teams" aggregate is appropriate. Admins /
    // supadmins fetch immediately.
    enabled: !isManager || !!teamId || managerAllTeams,
    refetchInterval: 30_000,
  });

  // T4 port (prototype Task #451) — warm the dashboard cache for the time
  // ranges immediately before / after the active one so a period switch
  // resolves from cache instead of waiting on a fresh request. Keys +
  // queryFn mirror the live ``q`` above exactly (incl. the normalised
  // compare / range-mode used per range) so a switch is a cache hit, not
  // a duplicate fetch.
  const queryClient = useQueryClient();
  useEffect(() => {
    // Mirror ``q.enabled`` so we never prefetch before a manager's scope
    // is resolved (which would fire an unscoped, cross-team request).
    if (isManager && !teamId && !managerAllTeams) return;
    if (timeRange === "custom") return; // no defined neighbour
    const idx = ADJACENT_PREFETCH_RANGES.indexOf(timeRange);
    if (idx === -1) return;
    const neighbours = [
      ADJACENT_PREFETCH_RANGES[idx - 1],
      ADJACENT_PREFETCH_RANGES[idx + 1],
    ].filter((r): r is TimeRange => Boolean(r));
    for (const r of neighbours) {
      // Match the component's own ``effectiveRangeMode`` / ``effectiveCompare``
      // normalisation for the target range so the cache key lines up.
      const mode: RangeMode =
        r === "daily" || r === "all_time" ? "calendar" : rangeMode;
      const cmp = compare && r !== "all_time";
      void queryClient.prefetchQuery({
        queryKey: [
          "analytics-dashboard",
          teamId || null,
          agentId || null,
          r,
          cmp,
          mode,
          null,
          null,
        ],
        queryFn: () =>
          api.analyticsDashboard(
            teamId || null,
            r,
            cmp,
            agentId || null,
            mode,
            null,
          ),
      });
    }
  }, [
    queryClient,
    timeRange,
    teamId,
    agentId,
    rangeMode,
    compare,
    isManager,
    managerAllTeams,
  ]);

  // Agent options for the drill-down dropdown — keyed on the team so it
  // refetches automatically and the previous team's agents never appear
  // in the new team's list.
  const agentsQ = useQuery({
    queryKey: ["analytics-team-agents", teamId || null],
    queryFn: () => api.analyticsTeamAgents(teamId),
    enabled: !!teamId,
    refetchOnWindowFocus: false,
  });

  // When the team list refreshes (or empties), drop any agent selection
  // that is no longer valid so the dashboard never sends a stale id.
  useEffect(() => {
    if (!agentId) return;
    const opts = agentsQ.data;
    if (opts && !opts.some((a) => a.agent_id === agentId)) {
      setAgentId("");
    }
  }, [agentId, agentsQ.data]);

  const onTeamChange = (next: string) => {
    setTeamId(next);
    // Resetting before the new team's data lands keeps the request
    // consistent with the dropdown's "All agents" default.
    setAgentId("");
  };

  const selectedAgentName = useMemo(() => {
    if (!agentId) return null;
    return (
      agentsQ.data?.find((a) => a.agent_id === agentId)?.agent_name || null
    );
  }, [agentId, agentsQ.data]);

  const selectedTeamName = useMemo(() => {
    if (!teamId) return null;
    const opt = q.data?.scope.team_options.find((t) => t.id === teamId);
    return opt ? opt.name : null;
  }, [teamId, q.data]);

  // Manager whose scope resolved to zero teams: nothing is in scope, and the
  // main dashboard query stays disabled, so without this we'd sit on
  // "Loading dashboard…" forever. Show a clear message instead. (Kept after the
  // scope query has actually succeeded so a genuinely in-flight scope request
  // still shows the loading state below.)
  if (
    isManager &&
    !teamId &&
    !managerAllTeams &&
    scopeQ.isSuccess &&
    managerTeamCount === 0
  ) {
    return (
      <div className="rounded-lg border border-border bg-muted/40 p-4 text-muted-foreground">
        <div className="font-medium text-foreground">No team assigned</div>
        <div className="text-sm mt-1">
          You're not assigned to a team yet. Ask an administrator to add you to a
          team, then your dashboard will appear here.
        </div>
      </div>
    );
  }
  if (
    (q.isLoading || (isManager && !teamId && !managerAllTeams)) &&
    !q.data
  ) {
    return <div className="text-muted-foreground">Loading dashboard…</div>;
  }
  if (q.error) {
    const msg = (q.error as { message?: string })?.message || "Failed to load dashboard";
    // The failure can be *caused by* the requested window itself — e.g. a
    // bookmarked custom range that ends before the retention floor gets a 400
    // — so the range controls are the recovery path and must stay mounted.
    // Everything else in the toolbar (compare toggle, Team/Agent filters)
    // needs `data` and is omitted here; picking any range re-runs the query.
    return (
      <div className="space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <h2 className="text-xl font-semibold text-foreground">Team View</h2>
          <TimeRangeControls
            value={timeRange}
            rangeMode={rangeMode}
            customRange={customRange}
            onValueChange={onTimeRangeChange}
            onRangeModeChange={onRangeModeChange}
            onCustomRangeChange={onCustomRangeChange}
          />
        </div>
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive">
          <div className="font-medium">Failed to load dashboard</div>
          <div className="text-sm mt-1">{msg}</div>
        </div>
      </div>
    );
  }
  const data = q.data!;
  const { metrics, scope, deltas } = data;

  const teamFunnelRows: TeamFunnelRow[] = data.team_viable
    .map((t) => ({
      team_id: t.team_id,
      team_name: t.team_name,
      registered: t.registered,
      viable: t.viable,
      quality: t.quality,
    }))
    // Alphabetical by team name so the row order is stable and easy to scan.
    .sort((a, b) => a.team_name.localeCompare(b.team_name));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Team View</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {agentId && selectedAgentName
              ? `Call analytics for ${selectedAgentName}${selectedTeamName ? ` · ${selectedTeamName}` : ""}`
              : isManager && !teamId && managerAllTeams
              ? `Call analytics across all ${scope.team_options.length} of your teams.`
              : `Call analytics across ${scope.team_options.length} team${scope.team_options.length === 1 ? "" : "s"} in your scope.`}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <TimeRangeControls
            value={timeRange}
            rangeMode={rangeMode}
            customRange={customRange}
            onValueChange={onTimeRangeChange}
            onRangeModeChange={onRangeModeChange}
            onCustomRangeChange={onCustomRangeChange}
            compare={{
              compare,
              period: data.period,
              priorPeriod: data.prior_period,
              onCompareChange: setCompare,
            }}
          />
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">Team</label>
            <select
              value={teamId}
              onChange={(e) => onTeamChange(e.target.value)}
              className="rounded border border-input bg-card px-2 py-1.5 text-sm"
            >
              {!isManager && (
                <option value="">All teams in scope</option>
              )}
              {managerAllTeams && (
                <option value="">All teams</option>
              )}
              {scope.team_options.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.community_name} — {t.name}
                </option>
              ))}
            </select>
          </div>
          {teamId ? (
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Agent</label>
              <select
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                disabled={agentsQ.isLoading}
                className="rounded border border-input bg-card px-2 py-1.5 text-sm disabled:bg-muted/40 disabled:text-muted-foreground"
              >
                <option value="">All agents</option>
                {(agentsQ.data ?? []).map((a) => (
                  <option key={a.agent_id} value={a.agent_id}>
                    {a.agent_name || "Unknown agent"}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:items-stretch">
        <div className="flex-1 lg:basis-3/5 min-w-0">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Dial Pad Funnel
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MetricCard
              label="Registered calls"
              value={metrics.registered.toLocaleString()}
              icon={<PhoneCall className="h-5 w-5" />}
              delta={deltas?.registered}
              tooltip="All calls made or received."
              metricId="registered"
              selected={funnelFilter === "registered"}
              onClick={() => toggleFunnel("registered")}
            />
            <MetricCard
              label="Viable calls"
              value={metrics.viable.toLocaleString()}
              sub={`${pct(metrics.viable, metrics.registered)} of registered`}
              icon={<CheckCircle2 className="h-5 w-5" />}
              tone="indigo"
              delta={deltas?.viable}
              tooltip="All calls that met a minimum duration."
              metricId="viable"
              selected={funnelFilter === "viable"}
              onClick={() => toggleFunnel("viable")}
            />
            <MetricCard
              label="Quality calls"
              value={metrics.quality.toLocaleString()}
              sub={`${pct(metrics.quality, metrics.viable)} of viable`}
              icon={<Sparkles className="h-5 w-5" />}
              tone="amber"
              delta={deltas?.quality}
              tooltip="Longer than one minute, connected to someone outside of the organization, have multiple voices detected, and determined to be business related."
              metricId="quality"
              selected={funnelFilter === "quality"}
              onClick={() => toggleFunnel("quality")}
            />
          </div>
        </div>
        <div
          className="hidden lg:block w-px self-stretch bg-border mx-1"
          aria-hidden="true"
        />
        <div className="lg:hidden h-px w-full bg-border" aria-hidden="true" />
        <div className="flex-1 lg:basis-2/5 min-w-0">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Sentiment
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <MetricCard
              label="Positive sentiment"
              value={metrics.positive.toLocaleString()}
              sub={`${pct(metrics.positive, metrics.viable)} of viable`}
              icon={<Smile className="h-5 w-5" />}
              tone="emerald"
              delta={deltas?.positive}
              tooltip="The customer has said something indicating they are pleased."
              metricId="positive_sentiment"
              selected={sentimentFilter === "positive"}
              onClick={() => toggleSentiment("positive")}
            />
            <MetricCard
              label="Negative sentiment"
              value={metrics.negative.toLocaleString()}
              sub={`${pct(metrics.negative, metrics.viable)} of viable`}
              icon={<Frown className="h-5 w-5" />}
              tone="rose"
              delta={deltas?.negative}
              tooltip="The customer has said something indicating they are frustrated or dissatisfied."
              metricId="negative_sentiment"
              selected={sentimentFilter === "negative"}
              onClick={() => toggleSentiment("negative")}
            />
          </div>
        </div>
      </div>

      <CollapsibleSection title="DialPad overview" defaultOpen={false}>
        {/* Wave 9 (port-plan 22) — compact recompose: ONE toggled volume
            card beside the funnel/sentiment pair at lg, stacked below. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TeamVolumeCard
            daily={data.daily}
            priorDaily={data.prior_daily}
            hourly={data.hourly}
            priorHourly={data.prior_hourly}
            weekday={data.weekday}
            priorWeekday={data.prior_weekday}
            timeRange={data.time_range}
            rangeMode={effectiveRangeMode}
            funnelFilter={funnelFilter}
            sentimentFilter={sentimentFilter}
          />

          <div className="space-y-4">
            <TeamCallFunnelBars rows={teamFunnelRows} />
            <TeamSentimentChart
              rows={data.team_sentiment}
              onSelect={(team, sentiment, trigger) =>
                setSentimentDrill({ team, sentiment, trigger })
              }
            />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Assessment overview" defaultOpen={false}>
        {(() => {
          // Task #213 — the backend reports All-time as
          // ``period.start = end = null`` (the contract other callers
          // rely on for "unbounded"). The Assessments queries are gated
          // on a concrete from/to, so forwarding the nulls leaves every
          // card blank. Resolve a 90-day window on the client that
          // matches the chart-tile fallback (``_ALL_TIME_CHART_DAYS``)
          // so the section actually renders.
          const { from: assessRangeFrom, to: assessRangeTo } =
            resolveAssessmentsRange(timeRange, data.period);
          return (
            <TeamAssessmentsSection
              teams={scope.team_options}
              selectedTeamId={teamId}
              selectedAgentId={agentId || null}
              rangeFrom={assessRangeFrom}
              rangeTo={assessRangeTo}
              timeRange={timeRange}
              rangeMode={rangeMode}
              onAgentDrillNav={onSelectAgentWithRange}
              viewerRole={me.role}
              onOpenCall={onOpenCall}
            />
          );
        })()}
      </CollapsibleSection>

      {sentimentDrill && (
        <SentimentBreakdownModal
          team={sentimentDrill.team}
          sentiment={sentimentDrill.sentiment}
          timeRange={timeRange}
          rangeMode={effectiveRangeMode}
          customRange={customRange}
          agentId={agentId || null}
          onClose={() => {
            const trigger = sentimentDrill.trigger;
            setSentimentDrill(null);
            // Return focus to the clicked segment so keyboard users
            // don't lose their place when the dialog closes.
            queueMicrotask(() => trigger?.focus());
          }}
        />
      )}
    </div>
  );
}
